import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { Button, Textfield } from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, Tooltip, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { useOnboardingContext } from 'context/OnboardingContext'
import mixpanel from 'mixpanel-browser'
import { setCookie } from 'nookies'
import { ActivateAccountFormsProps } from 'templates/ActivateAccount/ActivateAccount'

import { DatePicker } from 'components/DatePicker'
import InputController from 'components/InputController'

import { currency, Debouncer, onlyNumber } from 'utils'
import { normalizeDocument } from 'utils/document'
import { SESSION_COOKIE_OPTIONS } from 'utils/sessionCookie'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import {
  InformationFormSchema,
  schemaInformationForm
} from './Information.schema'
import * as S from './Information.styles'

export const initialInformationFormSchemaValue = {
  isCPF: true,
  document: '',
  legal_name: '',
  revenue: '0'
}

const InformationForm = ({
  sucessSubmit,
  handleBackStep
}: ActivateAccountFormsProps) => {
  const { values, handleChange, setPersonalizeStep, isStone } =
    useOnboardingContext()
  const handlePreviousStep = () => {
    handleBackStep && handleBackStep()
    setPersonalizeStep(2)
  }
  const { handleSubmit, control, watch, setValue } =
    useForm<InformationFormSchema>({
      defaultValues: values['1'],
      resolver: yupResolver(schemaInformationForm)
    })
  const revenueField = () => {
    return (
      <div>
        <Controller
          control={control}
          name="revenue"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Textfield
                label={
                  <Grid container={true} gap={'3px'} alignItems={'center'}>
                    <Grid item={true}>
                      Faturamento {isCNPJ && 'da empresa'}
                    </Grid>
                    <Grid item={true}>
                      <div>
                        <Tooltip
                          arrow
                          placement="top"
                          title={'Valor de faturamento projetado, mensal'}
                        >
                          <Box display={'flex'}>
                            <S.IconStyled icon="info" />
                          </Box>
                        </Tooltip>
                      </div>
                    </Grid>
                  </Grid>
                }
                mask="money"
                startAdornment={true}
                customStartAdornment={
                  <Typography mr="6px" variant="smLight">
                    R$
                  </Typography>
                }
                value={value}
                onChange={onChange}
                helperText={error?.message}
                error={!!error?.message}
              />
            )
          }}
        />
      </div>
    )
  }
  const birthdateField = () => {
    return (
      <Box gridRow={isCNPJ ? 3 : 'auto'}>
        <Controller
          control={control}
          name={'birthdate'}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <DatePicker
                helperText={error?.message}
                error={!!error?.message}
                label={
                  normalizeDocument(document || '').length < 14
                    ? 'Data de nascimento'
                    : 'Fundação da empresa'
                }
                maxDate={new Date()}
                onChange={onChange}
                renderInput={(props: any) => (
                  <Textfield {...props} name="birthdate" />
                )}
                toolbarPlaceholder="Complete com  a data de fundação da empresa"
                value={value ? value : null}
              />
            )
          }}
        />
      </Box>
    )
  }
  // SC-10941: renda mensal (pessoa física ou sócio) em R$, com a mesma máscara do
  // faturamento. Só aparece para quem está com o cartão na Stone.
  const moneyField = (
    name: 'monthly_income' | 'owner_monthly_income',
    label: string
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Textfield
          label={label}
          mask="money"
          startAdornment={true}
          customStartAdornment={
            <Typography mr="6px" variant="smLight">
              R$
            </Typography>
          }
          value={value ?? ''}
          onChange={onChange}
          helperText={error?.message}
          error={!!error?.message}
        />
      )}
    />
  )
  const { document, legal_name } = watch()
  const [isCNPJ, setIsCNPJ] = useState(
    normalizeDocument(document || '').length === 14
  )
  const awaitToSubmit = Debouncer(() => autoCompleteDocument(), 700)

  const onSubmit: SubmitHandler<InformationFormSchema> = async (data) => {
    const {
      revenue,
      monthly_income,
      professional_occupation,
      owner_phone,
      owner_monthly_income,
      owner_professional_occupation,
      ...fields
    } = data
    // eslint-disable-next-line
    const { isCPF, isStone: _isStone, ...rest } = fields
    // O replace dos milhares precisa ser global: com replace simples só o
    // primeiro ponto saía, e o remendo por tamanho da string (length > 8)
    // compensava isso acima de R$ 1 milhão às custas de multiplicar por 100
    // a faixa intermediária (ex.: "99.999,99" virava 9999999).
    const parseMoney = (value?: string | null) =>
      currency(value).replace(/\./g, '').replace(',', '.')
    const parsedValue = parseMoney(revenue)

    // SC-10941: os campos da Stone só vão para quem está na Stone — o backend só
    // grava o que recebe, e o fluxo da Zoop segue com o payload de sempre.
    const stoneFields = !isStone
      ? {}
      : isCNPJ
      ? {
          owner_phone: onlyNumber(owner_phone || ''),
          owner_monthly_income: parseMoney(owner_monthly_income),
          owner_professional_occupation
        }
      : {
          monthly_income: parseMoney(monthly_income),
          professional_occupation
        }

    const response = await DataService({
      type: 'PATCH',
      data: {
        ...rest,
        ...stoneFields,
        revenue: parsedValue
      },
      url: '/v1/onboarding/merchant'
    })
    setCookie(undefined, 'nextauth.document', document, SESSION_COOKIE_OPTIONS)

    handleChange(1, data)
    return sucessSubmit({ error: response?.error, message: response?.message })
  }
  const mixpanelTyped = mixpanel as unknown as { __loaded: string }
  const autoCompleteDocument = async () => {
    if (!document) return
    const url = `/v1/onboarding/enrich/${normalizeDocument(document)}`
    const response = await DataService({ url, type: 'GET' })
    if (!legal_name) setValue('legal_name', response?.data?.record?.name || '')
  }

  useEffect(() => {
    setValue('isStone', isStone)
  }, [isStone])

  useEffect(() => {
    setIsCNPJ(normalizeDocument(document || '').length === 14)
    setValue('isCPF', normalizeDocument(document || '').length === 11)
    awaitToSubmit('')
  }, [document])

  return (
    <S.Form onSubmit={handleSubmit(onSubmit, toastFirstError)}>
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography
          sx={{
            [theme.breakpoints.down(800)]: {
              fontSize: '18px'
            }
          }}
          variant="lgBold"
        >
          Você quer ativar a sua conta com CNPJ ou CPF?
        </Typography>
        <S.TextWrapper>
          <ul>
            <li>
              <Typography variant="smLight">
                CNPJ deve receber em uma conta bancária PJ, da mesma
                titularidade.
              </Typography>
            </li>
            <li>
              <Typography variant="smLight">
                CPF deve receber em uma conta bancária PF, da mesma
                titularidade.
              </Typography>
            </li>
          </ul>
        </S.TextWrapper>
      </Box>

      <S.InputWrapper>
        <InputController control={control} name="document">
          <Textfield
            label="CPF/CNPJ"
            placeholder="00.000.000/0000-00"
            mask="cpfCnpj"
          />
        </InputController>
        {isCNPJ && (
          <>
            <InputController control={control} name="legal_name">
              <Textfield label="Razão Social" />
            </InputController>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                {birthdateField()}
              </Grid>
              <Grid item xs={12} lg={6}>
                {revenueField()}
              </Grid>
            </Grid>

            <InputController control={control} name="owner_name">
              <Textfield
                placeholder="Digite aqui o nome do sócio"
                label="Nome do sócio"
              />
            </InputController>
            <Grid container spacing={2}>
              <Grid xs={12} lg={6} item>
                <InputController control={control} name="owner_document">
                  <Textfield
                    placeholder="00.000.000-00"
                    label="CPF do sócio"
                    mask="cpfCnpj"
                  />
                </InputController>
              </Grid>
              <Grid xs={12} lg={6} item>
                <Controller
                  control={control}
                  name={'owner_birthdate'}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => {
                    return (
                      <DatePicker
                        helperText={error?.message}
                        error={!!error?.message}
                        label={'Data de nascimento do sócio'}
                        maxDate={new Date()}
                        onChange={onChange}
                        renderInput={(props: any) => (
                          <Textfield {...props} name="owner_birthdate" />
                        )}
                        toolbarPlaceholder="Data de nascimento do sócio"
                        value={value ? value : null}
                      />
                    )
                  }}
                />
              </Grid>
            </Grid>
            {isStone && (
              <>
                <Grid container spacing={2}>
                  <Grid xs={12} lg={6} item>
                    <InputController control={control} name="owner_phone">
                      <Textfield
                        label="Telefone do sócio"
                        placeholder="(00) 00000-0000"
                      />
                    </InputController>
                  </Grid>
                  <Grid xs={12} lg={6} item>
                    {moneyField(
                      'owner_monthly_income',
                      'Renda mensal do sócio'
                    )}
                  </Grid>
                </Grid>
                <InputController
                  control={control}
                  name="owner_professional_occupation"
                >
                  <Textfield
                    label="Ocupação profissional do sócio"
                    placeholder="Ex.: Empresário"
                  />
                </InputController>
              </>
            )}
          </>
        )}

        {normalizeDocument(document || '').length === 11 && (
          <div>
            <InputController control={control} name="legal_name">
              <Textfield label="Nome completo" />
            </InputController>
          </div>
        )}
        {normalizeDocument(document || '').length === 11 && birthdateField()}
        {normalizeDocument(document || '').length === 11 && revenueField()}
        {isStone && normalizeDocument(document || '').length === 11 && (
          <>
            {moneyField('monthly_income', 'Renda mensal')}
            <InputController control={control} name="professional_occupation">
              <Textfield
                label="Ocupação profissional"
                placeholder="Ex.: Advogada"
              />
            </InputController>
          </>
        )}
      </S.InputWrapper>
      <Box mt={2} display="flex" gap={1.5} width="100%">
        <Button
          fullWidth
          variantButton="outlined"
          type="button"
          onClick={handlePreviousStep}
          sx={{
            backgroundColor: 'white'
          }}
        >
          Voltar
        </Button>
        <Box
          width="100%"
          onClick={() =>
            mixpanelTyped.__loaded &&
            mixpanel.track('Preencheu dados pessoais concluir cadastro')
          }
        >
          <Button fullWidth variantButton="primary" type="submit">
            Continuar
          </Button>
        </Box>
      </Box>
    </S.Form>
  )
}

export default InformationForm
